<!--
  PlanCard.vue -- SA Plans & Pricing card redesign, Unit R2.

  Card: A1 (Pricing & Splits - Subscription Billing Engine) / B (Admin UI).
  Authority: docs/planning/2026-08-04-sa-plans-pricing-card-redesign.md.

  One expanding/collapsing card per subscription plan, replacing the old
  split plans-table + separate per-tier decoration-grid tables. Mirrors the
  category-editor controlled-component contract
  (StoreCategories/CategoryCard.vue): this is a PURE, CONTROLLED
  presentational component -- it NEVER calls the API itself. The parent
  (PlansPricing.vue) owns every PATCH/POST and the single-open accordion
  state; this card takes the plan row + `expanded` as props and EMITS
  intents on user action. The parent decides how/when to persist (debounce
  text edits, immediate for toggles) -- exactly like CategoryCard.

  Header (always visible; clicking it toggles expand):
    plan name, pills (Hidden / Disabled / New), monthly price, the two
    independent toggles (Active/Disabled + Public/Hidden), a delete
    affordance (enabled only when subscriber_count == 0), and the
    expand chevron.

  Body (expanded): embroidery + DTF decoration grids SIDE BY SIDE for the
  plan's tier (read-only this pass; grid data passed down from the parent's
  decoration-grids fetch, matched by plan_id), the per-order app fee, and
  the inline-editable display name + monthly price (emit intent -> parent
  PATCHes; name debounced on blur, price on blur, mirroring CategoryCard).

  Two INDEPENDENT toggles:
    - Active/Disabled (is_active): the parent gates on subscriber count --
      disabling a plan with subscribers opens a reassign modal there; this
      card only emits `toggle-active`.
    - Public/Hidden (is_public): instant, emits `toggle-public`.
    - Default for new self-sign-ups (is_default_signup): ONE plan carries it;
      the body button emits `set-default-signup`, the header shows a pill.

  Delete: emits `delete-plan`; the parent confirms + archives. The control
  is DISABLED when subscriber_count > 0 (inline reason + tooltip).

  Grid-availability note: the decoration-grids endpoint returns grids only
  for the tier-a / tier-b plans (keyed by plan_key). A plan with no matched
  grid entry (a newly-created plan, or any non-tier plan) renders a
  "grids not shown" note instead of empty tables -- grid display/editing
  for arbitrary plans is a later surface (see the plan doc "Open /
  deferred").

  Test-id prefix: sa-plan-card-* (per-id -- ids carry the plan id so
  multiple cards on one page stay distinct, e.g. sa-plan-card-name-<id>).
-->
<template>
    <div
        class="plan-card"
        :class="{
            'plan-card--new': isNew,
            'plan-card--disabled': plan.is_active === false,
        }"
        :data-testid="`sa-plan-card-${plan.id}`"
    >
        <!-- ============================================================
             Header -- always visible; clicking it (outside the toggles /
             delete button) toggles expand/collapse.
             ============================================================ -->
        <div
            class="plan-card__header"
            :class="{ 'plan-card__header--expanded': expanded }"
            :data-testid="`sa-plan-card-header-${plan.id}`"
            role="button"
            tabindex="0"
            :aria-expanded="expanded ? 'true' : 'false'"
            @click="onToggleExpand"
            @keydown.enter.prevent="onToggleExpand"
            @keydown.space.prevent="onToggleExpand"
        >
            <!-- Reorder controls. The grip is the sortablejs handle (parent
                 binds it); the arrows are the keyboard / assistive-tech path
                 and emit the same reorder the drag does. -->
            <div class="plan-card__reorder" @click.stop @keydown.stop>
                <span
                    class="tf-icon plan-card__drag"
                    :data-testid="`sa-plan-card-drag-${plan.id}`"
                    title="Drag to reorder"
                    aria-hidden="true"
                    >drag_indicator</span
                >
                <button
                    type="button"
                    class="plan-card__move-btn"
                    :data-testid="`sa-plan-card-move-up-${plan.id}`"
                    :disabled="!canMoveUp"
                    :aria-label="`Move ${plan.name} up`"
                    title="Move up"
                    @click.stop="$emit('move-up')"
                >
                    <span class="tf-icon">arrow_upward</span>
                </button>
                <button
                    type="button"
                    class="plan-card__move-btn"
                    :data-testid="`sa-plan-card-move-down-${plan.id}`"
                    :disabled="!canMoveDown"
                    :aria-label="`Move ${plan.name} down`"
                    title="Move down"
                    @click.stop="$emit('move-down')"
                >
                    <span class="tf-icon">arrow_downward</span>
                </button>
            </div>
            <!-- The plan's stored card icon in its accent, via the same
                 component the public /pricing card uses. -->
            <PlanIcon
                class="plan-card__plan-icon"
                :plan="plan"
                :size="22"
                :test-id="`sa-plan-card-icon-${plan.id}`"
            />
            <div class="plan-card__heading">
                <div class="plan-card__title-row">
                    <span class="plan-card__title">{{
                        plan.name || "Untitled plan"
                    }}</span>
                    <span
                        v-if="plan.is_active === false"
                        class="plan-card__pill plan-card__pill--disabled"
                        :data-testid="`sa-plan-card-disabled-pill-${plan.id}`"
                    >Disabled</span>
                    <span
                        v-if="plan.is_public === false"
                        class="plan-card__pill plan-card__pill--hidden"
                        :data-testid="`sa-plan-card-hidden-pill-${plan.id}`"
                    >Hidden</span>
                    <span
                        v-if="isDefaultSignup"
                        class="plan-card__pill plan-card__pill--default"
                        :data-testid="`sa-plan-card-default-signup-pill-${plan.id}`"
                    >Default for new sign-ups</span>
                    <span
                        v-if="isNew"
                        class="plan-card__pill plan-card__pill--new"
                        :data-testid="`sa-plan-card-new-pill-${plan.id}`"
                    >New</span>
                </div>
                <div
                    class="plan-card__subtext"
                    :data-testid="`sa-plan-card-subscribers-${plan.id}`"
                >
                    {{ subscriberLabel }}
                </div>
                <div
                    v-if="!canDelete"
                    class="plan-card__subtext plan-card__delete-reason"
                    :data-testid="`sa-plan-card-delete-reason-${plan.id}`"
                >
                    {{ deleteBlockedReason }}
                </div>
            </div>

            <div class="plan-card__spacer" />

            <span
                class="plan-card__price"
                :data-testid="`sa-plan-card-price-${plan.id}`"
            ><template
                v-for="(part, i) in pricePartList"
                :key="part.unit"
            ><template v-if="i > 0"> + </template>{{ formatCents(part.cents)
            }}<span class="plan-card__price-per">{{ part.unit }}</span></template
            ><template v-if="!pricePartList.length">--</template></span>

            <!-- Active / Disabled toggle. Emits an intent; the parent gates
                 on subscriber count (opens the reassign modal if needed). -->
            <button
                type="button"
                class="plan-card__toggle-btn"
                :class="{ 'plan-card__toggle-btn--on': plan.is_active !== false }"
                :data-testid="`sa-plan-card-active-toggle-${plan.id}`"
                :title="
                    plan.is_active !== false
                        ? 'Active -- click to disable'
                        : 'Disabled -- click to re-activate'
                "
                @click.stop="onToggleActive"
            >
                <span class="tf-icon">{{
                    plan.is_active !== false ? "toggle_on" : "toggle_off"
                }}</span>
                {{ plan.is_active !== false ? "Active" : "Disabled" }}
            </button>

            <!-- Public / Hidden toggle. Instant, no modal. -->
            <button
                type="button"
                class="plan-card__toggle-btn"
                :class="{ 'plan-card__toggle-btn--on': plan.is_public !== false }"
                :data-testid="`sa-plan-card-public-toggle-${plan.id}`"
                :title="
                    plan.is_public !== false
                        ? 'Public -- click to hide from self-signup'
                        : 'Hidden -- click to publish'
                "
                @click.stop="onTogglePublic"
            >
                <span class="tf-icon">{{
                    plan.is_public !== false ? "visibility" : "visibility_off"
                }}</span>
                {{ plan.is_public !== false ? "Public" : "Hidden" }}
            </button>

            <!-- Delete (soft-delete / archive). Enabled only when NO
                 subscription (of any state) is on the plan. -->
            <button
                type="button"
                class="plan-card__delete-btn"
                :data-testid="`sa-plan-card-delete-${plan.id}`"
                :disabled="!canDelete"
                :title="
                    canDelete
                        ? 'Delete (archive) this plan'
                        : deleteBlockedReason
                "
                @click.stop="onDelete"
            >
                <span class="tf-icon">delete_outline</span>Delete
            </button>

            <button
                type="button"
                class="plan-card__expand-btn"
                :data-testid="`sa-plan-card-expand-${plan.id}`"
                :title="expanded ? 'Collapse' : 'Expand'"
                :aria-label="expanded ? 'Collapse plan' : 'Expand plan'"
                @click.stop="onToggleExpand"
            >
                <span class="tf-icon">{{
                    expanded ? "expand_less" : "expand_more"
                }}</span>
            </button>
        </div>

        <!-- ============================================================
             Body -- v-show (not v-if): stays mounted while collapsed so
             field-level event contracts keep working regardless of the
             visual collapse state (mirrors CategoryCard).
             ============================================================ -->
        <div
            v-show="expanded"
            class="plan-card__body"
            :data-testid="`sa-plan-card-body-${plan.id}`"
        >
            <!-- Core editable fields: display name + monthly price. Emit on
                 blur; the parent PATCHes. -->
            <div class="plan-card__fields">
                <div class="plan-card__field">
                    <label
                        class="plan-card__field-label"
                        :for="`sa-plan-card-name-${plan.id}`"
                    >Display name</label>
                    <input
                        :id="`sa-plan-card-name-${plan.id}`"
                        type="text"
                        class="plan-card__input"
                        :data-testid="`sa-plan-card-name-${plan.id}`"
                        :value="plan.name"
                        placeholder="Untitled plan"
                        @blur="onNameBlur"
                        @keydown.enter="$event.target.blur()"
                    />
                </div>

                <!--
                  Free-trial length (days) for NEW signups on this plan. Not
                  a price: saved in place (no plan version, allowed with live
                  subscribers). Existing subscriptions keep the trial end
                  they were given at signup.
                -->
                <div class="plan-card__field">
                    <label
                        class="plan-card__field-label"
                        :for="`sa-plan-card-trial-days-${plan.id}`"
                    >Free trial (days)</label>
                    <input
                        :id="`sa-plan-card-trial-days-${plan.id}`"
                        type="number"
                        min="0"
                        max="365"
                        step="1"
                        class="plan-card__input"
                        :data-testid="`sa-plan-card-trial-days-${plan.id}`"
                        :value="plan.trial_days"
                        @blur="onTrialDaysBlur"
                        @keydown.enter="$event.target.blur()"
                    />
                    <span
                        class="plan-card__field-hint"
                        :data-testid="`sa-plan-card-trial-days-hint-${plan.id}`"
                    >Applies to new signups only; existing subscriptions keep
                        their current trial end. 0 means no trial.</span>
                </div>

                <div class="plan-card__field">
                    <label
                        class="plan-card__field-label"
                        :for="`sa-plan-card-price-input-${plan.id}`"
                    >Monthly price</label>
                    <div class="plan-card__input-dollar">
                        <span class="plan-card__input-dollar-prefix">$</span>
                        <input
                            :id="`sa-plan-card-price-input-${plan.id}`"
                            type="number"
                            min="0"
                            step="0.01"
                            class="plan-card__input plan-card__input--dollar"
                            :data-testid="`sa-plan-card-price-input-${plan.id}`"
                            :value="priceDollars"
                            placeholder="0.00"
                            @blur="onPriceBlur"
                            @keydown.enter="$event.target.blur()"
                        />
                    </div>
                    <button
                        v-if="plan.monthly_price_cents != null"
                        type="button"
                        class="plan-card__link-btn"
                        :data-testid="`sa-plan-card-price-remove-${plan.id}`"
                        @click="onRemovePrice"
                    >Remove monthly fee</button>
                    <span
                        class="plan-card__field-hint"
                        :data-testid="`sa-plan-card-price-hint-${plan.id}`"
                    >Changing the price, or using Remove to charge no monthly fee,
                        publishes a new plan version. An empty or invalid entry
                        is ignored.</span>
                </div>

                <!--
                  Per-store class prices (spec store-counting-rules-2026-10-15
                  section 3.1): "Per active store" (a store with an order in
                  the month or open 24h+) and "Per inactive store"
                  (a store that is not live this month, not archived). Same
                  blur-to-save idiom as the
                  monthly price, but a change PUBLISHES A NEW PLAN VERSION
                  (the parent POSTs .../store-class-price) -- distributors on
                  earlier versions keep their price until repointed, which
                  the hint says so the admin is not surprised.
                -->
                <div class="plan-card__field">
                    <label
                        class="plan-card__field-label"
                        :for="`sa-plan-card-active-store-price-input-${plan.id}`"
                    >Per active store</label>
                    <div class="plan-card__input-dollar">
                        <span class="plan-card__input-dollar-prefix">$</span>
                        <input
                            :id="`sa-plan-card-active-store-price-input-${plan.id}`"
                            type="number"
                            min="0"
                            step="0.01"
                            class="plan-card__input plan-card__input--dollar"
                            :data-testid="`sa-plan-card-active-store-price-input-${plan.id}`"
                            :value="storeClassDollars('full')"
                            placeholder="--"
                            @blur="onStoreClassPriceBlur('full', $event)"
                            @keydown.enter="$event.target.blur()"
                        />
                    </div>
                </div>

                <div class="plan-card__field">
                    <label
                        class="plan-card__field-label"
                        :for="`sa-plan-card-dormant-store-price-input-${plan.id}`"
                    >Per inactive store</label>
                    <div class="plan-card__input-dollar">
                        <span class="plan-card__input-dollar-prefix">$</span>
                        <input
                            :id="`sa-plan-card-dormant-store-price-input-${plan.id}`"
                            type="number"
                            min="0"
                            step="0.01"
                            class="plan-card__input plan-card__input--dollar"
                            :data-testid="`sa-plan-card-dormant-store-price-input-${plan.id}`"
                            :value="storeClassDollars('dormant')"
                            placeholder="--"
                            @blur="onStoreClassPriceBlur('dormant', $event)"
                            @keydown.enter="$event.target.blur()"
                        />
                    </div>
                </div>

                <!--
                  Included stores (flat-fee design M1/Q5): how many ACTIVE
                  stores are free before the per-active-store price starts.
                  Inactive stores never use it. It lives on the per-active-
                  store price, so with no such price there is nothing to
                  edit (disabled, with the reason). Same blur-to-save idiom;
                  a change PUBLISHES A NEW PLAN VERSION.
                -->
                <div class="plan-card__field">
                    <label
                        class="plan-card__field-label"
                        :for="`sa-plan-card-included-stores-input-${plan.id}`"
                    >Included stores</label>
                    <input
                        :id="`sa-plan-card-included-stores-input-${plan.id}`"
                        type="number"
                        min="0"
                        step="1"
                        class="plan-card__input"
                        :data-testid="`sa-plan-card-included-stores-input-${plan.id}`"
                        :value="includedStoresValue"
                        :disabled="!hasIncludedStores"
                        placeholder="--"
                        @blur="onIncludedStoresBlur"
                        @keydown.enter="$event.target.blur()"
                    />
                    <span
                        class="plan-card__field-hint"
                        :data-testid="`sa-plan-card-included-stores-note-${plan.id}`"
                    >{{
                        hasIncludedStores
                            ? "Active stores that are free; each one beyond this pays the per active store price."
                            : "Set a per active store price first; the included stores are part of it."
                    }}</span>
                </div>

                <div class="plan-card__field">
                    <span class="plan-card__field-label">Per-order app fee</span>
                    <span
                        class="plan-card__fee"
                        :data-testid="`sa-plan-card-app-fee-${plan.id}`"
                    >{{ appFeeLabel }}</span>
                </div>

                <!--
                  Platform-fee override (SuperAdmin only; the backend also
                  body-enforces the capability). THREE distinct states, and
                  the UI has to keep them distinct or it lies about what the
                  plan charges:
                    empty box   -> no override, the global rate applies
                    0           -> the platform fee is genuinely zero
                    2           -> 2% for this plan
                  The box is in PERCENT (what an admin thinks in); the API
                  carries a FRACTION. Clearing is a separate control from
                  typing 0 so a zeroed plan can be restored.
                -->
                <div class="plan-card__field">
                    <label
                        class="plan-card__field-label"
                        :for="`sa-plan-card-fee-override-input-${plan.id}`"
                    >Platform fee override</label>
                    <div class="plan-card__input-dollar">
                        <input
                            :id="`sa-plan-card-fee-override-input-${plan.id}`"
                            type="number"
                            min="0"
                            max="50"
                            step="0.01"
                            class="plan-card__input plan-card__input--dollar"
                            :data-testid="`sa-plan-card-fee-override-input-${plan.id}`"
                            :value="feeOverridePercent"
                            placeholder="Global"
                            @blur="onFeeOverrideBlur"
                            @keydown.enter="$event.target.blur()"
                        />
                        <span class="plan-card__input-dollar-prefix">%</span>
                    </div>
                    <span
                        class="plan-card__fee-override-state"
                        :data-testid="`sa-plan-card-fee-override-state-${plan.id}`"
                    >{{ feeOverrideLabel }}</span>
                    <button
                        v-if="hasFeeOverride"
                        type="button"
                        class="plan-card__link-btn"
                        :data-testid="`sa-plan-card-fee-override-clear-${plan.id}`"
                        @click="onClearFeeOverride"
                    >
                        Use global rate
                    </button>
                </div>

                <!--
                  ThreadFlow's shipping surcharge on this plan (design section
                  20.7): carrier rate + markup % of it + a flat handling fee
                  per order. SuperAdmin only (the backend body-enforces it).
                  Not a subscription price: saved in place, no plan version.
                  Both columns are NOT NULL, so there is no "clear" state:
                  0 is a real zero and a blank box restores the saved value.
                  The markup is a WHOLE percent (the pricing engine reads it
                  as an integer); the handling box is in dollars and the API
                  carries cents.
                -->
                <div class="plan-card__field">
                    <label
                        class="plan-card__field-label"
                        :for="`sa-plan-card-shipping-markup-input-${plan.id}`"
                    >Shipping markup</label>
                    <div class="plan-card__input-dollar">
                        <input
                            :id="`sa-plan-card-shipping-markup-input-${plan.id}`"
                            type="number"
                            min="0"
                            :max="SHIPPING_MARKUP_PCT_MAX"
                            step="1"
                            class="plan-card__input plan-card__input--dollar"
                            :data-testid="`sa-plan-card-shipping-markup-input-${plan.id}`"
                            :value="shippingMarkupValue"
                            @blur="onShippingMarkupBlur"
                            @keydown.enter="$event.target.blur()"
                        />
                        <span class="plan-card__input-dollar-prefix">%</span>
                    </div>
                </div>

                <div class="plan-card__field">
                    <label
                        class="plan-card__field-label"
                        :for="`sa-plan-card-shipping-handling-input-${plan.id}`"
                    >Shipping handling fee</label>
                    <div class="plan-card__input-dollar">
                        <span class="plan-card__input-dollar-prefix">$</span>
                        <input
                            :id="`sa-plan-card-shipping-handling-input-${plan.id}`"
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            class="plan-card__input plan-card__input--dollar"
                            :data-testid="`sa-plan-card-shipping-handling-input-${plan.id}`"
                            :value="shippingHandlingDollars"
                            @blur="onShippingHandlingBlur"
                            @keydown.enter="$event.target.blur()"
                        />
                    </div>
                    <span
                        class="plan-card__field-hint"
                        :data-testid="`sa-plan-card-shipping-surcharge-hint-${plan.id}`"
                    >ThreadFlow's shipping surcharge for distributors on this
                        plan: the handling fee once per order ($0.00 to
                        $100.00), plus, on ThreadFlow-carrier shipping only,
                        this markup as a whole percent of the carrier rate (0
                        to 100). A distributor's own override, if set, wins. A
                        change applies to every current subscriber's next paid
                        order. Not yet added to what shoppers pay at checkout.</span>
                </div>
            </div>

            <!--
              Description: the rich-text copy on this plan's card on the PUBLIC
              /pricing page. Not a price: saved in place with an explicit Save
              (a rich-text editor has no natural blur-to-save moment), allowed
              with live subscribers. Bold, italic, lists, link and line break
              only, 600 visible characters; the API sanitizes and enforces the
              cap, this field just says so before the round trip. Blank =
              the page shows its default copy for the plan.
            -->
            <div
                class="plan-card__description"
                :data-testid="`sa-plan-card-description-${plan.id}`"
            >
                <span class="plan-card__field-label">Description</span>
                <!-- Mounted only while the card is open: the accordion allows
                     one open card, so the editor's own test ids (rte-*) are
                     unique on the page and N editors are not built up front. -->
                <RichTextEditor
                    v-if="expanded"
                    v-model="descriptionDraft"
                    toolbar="basic"
                    :max-chars="DESCRIPTION_MAX_CHARS"
                    :data-testid="`sa-plan-card-description-editor-${plan.id}`"
                />
                <span class="plan-card__field-hint">Shown on this plan's card on
                    the public pricing page. Leave blank to use the default
                    text.</span>
                <p
                    v-if="descriptionTooLong"
                    class="plan-card__error"
                    :data-testid="`sa-plan-card-description-error-${plan.id}`"
                >
                    Too long: {{ descriptionLength }} of
                    {{ DESCRIPTION_MAX_CHARS }} characters. Shorten it to save.
                </p>
                <div class="plan-card__description-actions">
                    <button
                        type="button"
                        class="plan-card__toggle-btn plan-card__toggle-btn--on"
                        :data-testid="`sa-plan-card-description-save-${plan.id}`"
                        :disabled="!descriptionDirty || descriptionTooLong"
                        @click="onSaveDescription"
                    >
                        Save description
                    </button>
                </div>
            </div>

            <!--
              Card icon + accent color on the PUBLIC /pricing page (Jake,
              2026-10-20). Stored on the plan, so reordering plans never
              changes them. Only allow-listed keys are offered (the API also
              refuses anything else). Not a price: saves on click, like the
              Public / Active toggles, allowed with live subscribers. Every
              option is a native button (Tab + Enter/Space), aria-pressed
              marks the current one.
            -->
            <div
                class="plan-card__presentation"
                :data-testid="`sa-plan-card-presentation-${plan.id}`"
            >
                <span class="plan-card__field-label">Pricing card icon and color</span>
                <div
                    class="plan-card__presentation-preview"
                    :style="{ '--plan-accent': presentation.accentHex }"
                    :data-testid="`sa-plan-card-presentation-preview-${plan.id}`"
                    aria-hidden="true"
                >
                    <span
                        class="plan-card__presentation-preview-band"
                        :style="PREVIEW_BAND_TEXT"
                        :data-testid="`sa-plan-card-presentation-preview-band-${plan.id}`"
                    >{{
                        plan.name || "Untitled plan"
                    }}</span>
                    <PlanIcon :plan="plan" :size="40" />
                </div>

                <span
                    :id="`sa-plan-card-icon-label-${plan.id}`"
                    class="plan-card__presentation-sub"
                >Icon</span>
                <div
                    class="plan-card__icon-grid"
                    role="group"
                    :aria-labelledby="`sa-plan-card-icon-label-${plan.id}`"
                >
                    <button
                        v-for="opt in iconOptions"
                        :key="opt.key"
                        type="button"
                        class="plan-card__icon-option"
                        :class="{
                            'plan-card__icon-option--on':
                                opt.key === presentation.iconKey,
                        }"
                        :aria-pressed="opt.key === presentation.iconKey ? 'true' : 'false'"
                        :aria-label="`Icon: ${opt.label}`"
                        :title="opt.label"
                        :data-testid="`sa-plan-card-icon-option-${plan.id}-${opt.key}`"
                        @click="onPickIcon(opt.key)"
                    >
                        <span class="tf-icon" aria-hidden="true">{{ opt.key }}</span>
                    </button>
                </div>

                <span
                    :id="`sa-plan-card-accent-label-${plan.id}`"
                    class="plan-card__presentation-sub"
                >Color</span>
                <div
                    class="plan-card__accent-row"
                    role="group"
                    :aria-labelledby="`sa-plan-card-accent-label-${plan.id}`"
                >
                    <button
                        v-for="opt in accentOptions"
                        :key="opt.key"
                        type="button"
                        class="plan-card__accent-option"
                        :class="{
                            'plan-card__accent-option--on':
                                opt.key === presentation.accentKey,
                        }"
                        :style="{ backgroundColor: opt.hex }"
                        :aria-pressed="opt.key === presentation.accentKey ? 'true' : 'false'"
                        :aria-label="`Color: ${opt.label}`"
                        :title="opt.label"
                        :data-testid="`sa-plan-card-accent-option-${plan.id}-${opt.key}`"
                        @click="onPickAccent(opt.key)"
                    >
                        <span
                            v-if="opt.key === presentation.accentKey"
                            class="tf-icon"
                            aria-hidden="true"
                        >check</span>
                    </button>
                </div>
            </div>

            <p
                class="plan-card__store-class-hint"
                :data-testid="`sa-plan-card-store-class-hint-${plan.id}`"
            >
                Changing the monthly price, a per-store price or the included
                stores publishes a new plan version. Distributors on earlier
                versions keep their terms until you repoint them below.
            </p>

            <!-- Default for new self-sign-ups: exactly one plan carries it
                 (radio semantics -- choosing this one clears the previous
                 default, server-side). Disabled on the current default: the
                 way to move it is to choose another plan. The server refuses
                 a private / disabled / archived plan with a plain message,
                 shown in the error line just below. -->
            <div class="plan-card__default-signup">
                <button
                    type="button"
                    class="plan-card__toggle-btn"
                    :class="{ 'plan-card__toggle-btn--on': isDefaultSignup }"
                    :data-testid="`sa-plan-card-default-signup-${plan.id}`"
                    :aria-pressed="isDefaultSignup ? 'true' : 'false'"
                    :disabled="isDefaultSignup"
                    @click="onSetDefaultSignup"
                >
                    <span class="tf-icon">{{
                        isDefaultSignup
                            ? "radio_button_checked"
                            : "radio_button_unchecked"
                    }}</span>
                    Default for new self-sign-ups
                </button>
                <span class="plan-card__default-signup-hint">
                    {{
                        isDefaultSignup
                            ? "New self-sign-up distributors land on this plan. To change it, choose another plan."
                            : "Make this the plan new self-sign-up distributors land on. It must be public and active."
                    }}
                </span>
            </div>

            <p
                v-if="saveError"
                class="plan-card__error"
                :data-testid="`sa-plan-card-save-error-${plan.id}`"
            >
                {{ saveError }}
            </p>

            <!--
              Supplier availability for THIS plan's tier.

              Moved in from a standalone page-level matrix 2026-08-29 (Jake:
              "I expected supplier availability to be inside the expandable
              cards for each subscription plan"). The old table needed a
              "Plan" column to say which plan each row belonged to; a card
              has already answered that by being the plan, so the column --
              duplicated state -- goes away rather than getting restyled.

              EVERY supplier renders, checked or not. A supplier omitted
              from the list would be a decision nobody can see or reverse,
              which is the exact failure the tier-carries-the-list model is
              guarded against. The cross-tier half of that guard (a supplier
              in NO plan) stays PAGE-LEVEL in PlansPricing.vue: it is a fact
              about the set of plans, and the supplier it names is by
              definition absent from every card.

              Controlled component, same contract as the rest of this card:
              the toggles emit intents and the parent owns the matrix and
              every PUT. `suppliers` is the parent's OWN tier object, so two
              plans sharing a tier are two views of one row and cannot
              disagree.
            -->
            <div class="plan-card__suppliers-head">
                <span class="plan-card__grids-title">Supplier availability</span>
                <span class="plan-card__grids-hint">
                    Which suppliers this plan includes. A distributor can be
                    given a different answer individually on their detail
                    page.
                </span>
            </div>

            <p
                v-if="suppliersError"
                class="plan-card__error"
                :data-testid="`sa-plan-card-suppliers-error-${plan.id}`"
            >
                {{ suppliersError }}
            </p>

            <p
                v-else-if="suppliersLoading"
                class="plan-card__grids-empty"
                :data-testid="`sa-plan-card-suppliers-loading-${plan.id}`"
            >
                Loading supplier availability...
            </p>

            <!--
              No tier mapping = no honest answer. An empty list here would
              read as "this plan includes no suppliers", a confident wrong
              answer for the long-tail record (design-philosophy #5), so say
              what is actually true instead.
            -->
            <p
                v-else-if="!supplierTier"
                class="plan-card__grids-empty"
                :data-testid="`sa-plan-card-suppliers-unmapped-${plan.id}`"
            >
                Supplier availability is not shown for this plan: it has no
                pricing tier yet, and suppliers are configured per tier.
            </p>

            <template v-else>
                <table
                    class="plan-card__suppliers"
                    :data-testid="`sa-plan-card-suppliers-${plan.id}`"
                >
                    <thead>
                        <tr>
                            <th>Supplier</th>
                            <th>Included</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="supplier in supplierTier.suppliers"
                            :key="supplier.supplier_key"
                            :data-testid="`sa-plan-card-supplier-row-${plan.id}-${supplier.supplier_key}`"
                        >
                            <td>
                                {{ supplier.name }}
                                <div class="plan-card__supplier-key">
                                    {{ supplier.supplier_key }}
                                </div>
                            </td>
                            <td>
                                <input
                                    type="checkbox"
                                    :checked="supplier.is_enabled"
                                    :data-testid="`sa-plan-card-supplier-toggle-${plan.id}-${supplier.supplier_key}`"
                                    @change="onSupplierToggle(supplier, $event)"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div class="plan-card__suppliers-actions">
                    <button
                        type="button"
                        class="plan-card__suppliers-save"
                        :disabled="suppliersSaving || !suppliersDirty"
                        :data-testid="`sa-plan-card-suppliers-save-${plan.id}`"
                        @click="onSaveSuppliers"
                    >
                        {{ suppliersSaving ? "Saving..." : "Save supplier availability" }}
                    </button>
                    <span
                        v-if="suppliersSaved"
                        class="plan-card__suppliers-saved"
                        :data-testid="`sa-plan-card-suppliers-saved-${plan.id}`"
                    >
                        Saved.
                    </span>
                </div>
            </template>

            <!--
              White-label storefront branding for THIS plan's tier.

              Jake, 2026-09-18/19: "We need the ability to enable a
              distributor to change the branding of their storefronts from
              ThreadFlow to a brand associated with themselves or the store
              they're building. This should be a feature of the subscription
              tier system, and should default to off/disabled."

              Beside supplier availability, in the card, for the reason that
              one is here (Jake 2026-08-29: "I expected supplier availability
              to be inside the expandable cards for each subscription plan").
              A page-level tier x capability matrix is the shape he rejected.

              SAVES ON CLICK -- no Save button, unlike suppliers above. The
              difference is the data, not a style choice: a supplier LIST has
              meaningful half-edited states, so it earns a dirty flag and an
              explicit save. This is one boolean with no intermediate state,
              so a Save button could only confirm the click before it. The
              card already saves `is_public` and `is_active` this way; a
              third save idiom would be a second mechanism for one concept
              (design-philosophy #2).

              The copy states WHY flipping this is safe, deliberately: an
              admin who does not know reasonably assumes ordering matters
              and waits for stores to upload logos first.

              UPDATED 2026-09-20 (Unit 8). The reason CHANGED even though
              the guarantee did not. It used to be the logo fallback -- an
              enabled tier with no store logo showed the ThreadFlow logo.
              Jake replaced that rule ("If the toggle is on, no ThreadFlow
              logo at all"), and the safety now comes from the PER-STORE
              TOGGLE, which is off for every store until its distributor
              turns it on. Saying "the ThreadFlow logo is shown" here would
              now be simply false.
            -->
            <div
                class="plan-card__wl-head"
                :data-testid="`sa-plan-card-white-label-${plan.id}`"
            >
                <div>
                    <span class="plan-card__grids-title">
                        White-label branding
                    </span>
                    <span class="plan-card__grids-hint">
                        Lets stores on this plan replace ThreadFlow branding
                        with their own. Each store stays on ThreadFlow
                        branding until its own white-label switch is turned
                        on, so turning this on is safe at any time.
                    </span>
                </div>

                <!--
                  The switch renders only when the plan HAS a tier. The note
                  below covers the other case; a switch there would be a
                  control that writes nowhere (cf. PR #2922).
                -->
                <label
                    v-if="tierKey"
                    class="plan-card__wl-switch"
                >
                    <input
                        type="checkbox"
                        :checked="whiteLabelEnabled"
                        :disabled="whiteLabelLoading || whiteLabelSaving"
                        :data-testid="`sa-plan-card-white-label-toggle-${plan.id}`"
                        @change="onWhiteLabelToggle"
                    />
                    <span>{{ whiteLabelSaving ? "Saving..." : "Enabled" }}</span>
                    <!--
                      An instant save with no feedback is an invisible save:
                      the admin cannot tell "written" from "about to be
                      written" from "quietly failed", and the switch looks
                      identical in all three. This is the confirmation the
                      supplier section gets from its explicit Save button.

                      It is also the only element that APPEARS when the write
                      lands, which is what a ui-runner scenario can bind on:
                      `expect_db` is single-shot with no retry (verified in
                      tools/ui-runner/ui_runner/steps/expect_db.py -- no poll
                      loop), so reading the row straight after the click is a
                      race. Without this element there is nothing to wait on.
                    -->
                    <span
                        v-if="whiteLabelSaved"
                        class="plan-card__wl-saved"
                        :data-testid="`sa-plan-card-white-label-saved-${plan.id}`"
                    >
                        Saved.
                    </span>
                </label>
            </div>

            <p
                v-if="whiteLabelError"
                class="plan-card__error"
                :data-testid="`sa-plan-card-white-label-error-${plan.id}`"
            >
                {{ whiteLabelError }}
            </p>

            <p
                v-if="!tierKey"
                class="plan-card__grids-empty"
                :data-testid="`sa-plan-card-white-label-unmapped-${plan.id}`"
            >
                White-label branding is not shown for this plan: it has no
                pricing tier yet, and branding is configured per tier.
            </p>

            <!-- Digitizing / vectorizing charges (Trello #572), per tier. -->
            <TierArtServicesSettings
                :plan-id="plan.id"
                :tier-key="tierKey"
                :setting="artServices"
                :loading="artServicesLoading"
                :saving="artServicesSaving"
                :saved="artServicesSaved"
                :error="artServicesError"
                @save="(tier, setting) => $emit('save-art-services', tier, setting)"
            />

            <!-- Decoration grids, SIDE BY SIDE (embroidery + DTF), read-only. -->
            <div class="plan-card__grids-head">
                <span class="plan-card__grids-title">Decoration pricing</span>
                <span class="plan-card__grids-hint">
                    Platform price per piece per location, by quantity break.
                    Read-only.
                </span>
            </div>

            <p
                v-if="!hasGrids"
                class="plan-card__grids-empty"
                :data-testid="`sa-plan-card-grids-unavailable-${plan.id}`"
            >
                Decoration grids are not shown for this plan. Grid display for
                custom plans is a later pass (new plans price decoration at
                Tier A rates in the interim).
            </p>

            <div
                v-else
                class="plan-card__grids"
                :data-testid="`sa-plan-card-grids-${plan.id}`"
            >
                <div
                    v-for="grid in orderedGrids"
                    :key="grid.decoration_type"
                    class="plan-card__grid-col"
                >
                    <!-- Embroidery: 2-D qty rows x stitch-band columns. -->
                    <div
                        v-if="grid.decoration_type === 'embroidery'"
                        class="plan-card__grid-block"
                        :data-testid="`sa-plan-card-grid-embroidery-${plan.id}`"
                    >
                        <h4 class="plan-card__grid-block-title">
                            Embroidery (qty x stitch band)
                        </h4>
                        <table class="plan-card__grid-table">
                            <thead>
                                <tr>
                                    <th>Qty</th>
                                    <th
                                        v-for="band in embroideryBands(grid)"
                                        :key="band.key"
                                    >
                                        {{ band.label }}
                                    </th>
                                    <th>+1k stitch</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="row in grid.qty_tiers"
                                    :key="row.tier_index"
                                    :data-testid="`sa-plan-card-grid-embroidery-row-${plan.id}`"
                                >
                                    <td>{{ qtyLabel(row) }}</td>
                                    <td
                                        v-for="cell in row.stitch_tiers"
                                        :key="cell.stitch_min"
                                        :data-testid="`sa-plan-card-grid-embroidery-cell-${plan.id}`"
                                    >
                                        {{ formatCents(cell.platform_price_cents) }}
                                    </td>
                                    <td>{{ formatCents(row.overage_rate_cents) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- DTF: 1-D qty. -->
                    <div
                        v-else-if="grid.decoration_type === 'dtf'"
                        class="plan-card__grid-block"
                        :data-testid="`sa-plan-card-grid-dtf-${plan.id}`"
                    >
                        <h4 class="plan-card__grid-block-title">
                            DTF (qty only)
                        </h4>
                        <table class="plan-card__grid-table">
                            <thead>
                                <tr>
                                    <th>Qty</th>
                                    <th>Platform price</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="row in grid.qty_tiers"
                                    :key="row.tier_index"
                                    :data-testid="`sa-plan-card-grid-dtf-row-${plan.id}`"
                                >
                                    <td>{{ qtyLabel(row) }}</td>
                                    <td
                                        :data-testid="`sa-plan-card-grid-dtf-cell-${plan.id}`"
                                    >
                                        {{ formatCents(row.platform_price_cents) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatCents, planPriceParts } from "@/utils/planPrice";
import RichTextEditor from "@/components/RichTextEditor.vue";
import PlanIcon from "@/components/Pricing/PlanIcon.vue";
import TierArtServicesSettings from "./TierArtServicesSettings.vue";
import {
    PLAN_ACCENTS,
    PLAN_ICONS,
    resolvePlanPresentation,
} from "@/utils/planPresentation";
import { dollarsToCents } from "@/utils/dollarsToCents";
import {
    PLAN_DESCRIPTION_MAX_CHARS,
    planDescriptionLength,
} from "@/utils/sanitizePlanDescription";

// The API's bounds for the plan shipping surcharge (superadmin_pricing.py):
// a whole percent 0..100 and 0..10000 cents ($100.00) per order.
const SHIPPING_MARKUP_PCT_MAX = 100;
const SHIPPING_HANDLING_CENTS_MAX = 10000;

// Subscription states in display order, with reader-facing wording. A state
// the API adds later still renders (raw key) after these.
const SUBSCRIBER_STATE_ORDER = [
    "active",
    "trial",
    "payment_failed",
    "read_only",
    "suspended",
    "cancelled",
];
const SUBSCRIBER_STATE_LABELS = {
    active: "active",
    trial: "trial",
    payment_failed: "payment failed",
    read_only: "read-only",
    suspended: "suspended",
    cancelled: "cancelled",
};

export default {
    name: "PlanCard",

    components: { RichTextEditor, PlanIcon, TierArtServicesSettings },

    props: {
        // The plan row from GET /api/superadmin/pricing/plans:
        //   { id, name, monthly_price_cents, store_limit, included_orders,
        //     overage_cents, is_public, is_active, subscriber_count,
        //     subscribers_by_state }
        plan: { type: Object, required: true },
        // The matched decoration-grids entry for this plan (or null when the
        // endpoint returns none for it). Shape:
        //   { plan_id, plan_key, plan_name, is_public, grids: [...] }
        gridTier: { type: Object, default: null },
        // Per-order application fee in basis points (display-only this pass);
        // null when unknown.
        appFeeBasisPoints: { type: Number, default: null },
        isNew: { type: Boolean, default: false },
        // The global platform fee as a fraction (0.0395), owned by the page;
        // null until loaded or when none is configured.
        globalPlatformFeeRate: { type: Number, default: null },
        // Single-open accordion: whether THIS card is the open one. Owned by
        // the parent; this card only emits toggle-expand.
        expanded: { type: Boolean, default: false },
        // Reorder arrows (parent-owned: position in the list + save in flight).
        canMoveUp: { type: Boolean, default: false },
        canMoveDown: { type: Boolean, default: false },
        // A parent-owned, per-plan save error to surface inline (e.g. a 409
        // from an inline name/price PATCH). Empty string when none.
        saveError: { type: String, default: "" },

        // ---- Supplier availability (parent-owned, same controlled contract)
        // THIS plan's tier row from the tier x supplier matrix:
        //   { tier_key, tier_name, is_active, suppliers: [...] }
        // null when the plan maps to no tier -- rendered as an explicit
        // "not shown, no tier" note rather than an empty list, because an
        // empty list would read as "includes no suppliers".
        //
        // This is the PARENT's object, not a copy: two plans on one tier are
        // two views of the same row and so cannot disagree (design-philosophy
        // #4 -- agreement by single source, not by syncing).
        supplierTier: { type: Object, default: null },
        suppliersLoading: { type: Boolean, default: false },
        suppliersSaving: { type: Boolean, default: false },
        suppliersSaved: { type: Boolean, default: false },
        suppliersDirty: { type: Boolean, default: false },
        suppliersError: { type: String, default: "" },

        // ---- White-label branding (parent-owned, same controlled contract)
        // The flag belongs to the TIER, so the card needs `tierKey` to name
        // what it is toggling. `null` means this plan has no tier mapping --
        // rendered as a note rather than a switch that would write nowhere.
        tierKey: { type: String, default: null },
        whiteLabelEnabled: { type: Boolean, default: false },
        whiteLabelLoading: { type: Boolean, default: false },
        whiteLabelSaving: { type: Boolean, default: false },
        whiteLabelSaved: { type: Boolean, default: false },
        whiteLabelError: { type: String, default: "" },

        // ---- Digitizing / vectorizing charges (Trello #572), parent-owned.
        // The tier's row ({billing_mode, digitizing_cents, vectorizing_cents})
        // or null while loading.
        artServices: { type: Object, default: null },
        artServicesLoading: { type: Boolean, default: false },
        artServicesSaving: { type: Boolean, default: false },
        artServicesSaved: { type: Boolean, default: false },
        artServicesError: { type: String, default: "" },
    },

    emits: [
        "move-up",
        "move-down",
        "toggle-expand",
        "toggle-active",
        "toggle-public",
        "set-default-signup",
        "delete-plan",
        "update-name",
        "update-trial-days",
        "update-description",
        "update-presentation",
        "update-price",
        "update-store-class-price",
        "update-included-stores",
        "update-fee-override",
        "update-shipping-surcharge",
        "toggle-supplier",
        "save-suppliers",
        "toggle-white-label",
        "save-art-services",
    ],

    data() {
        return {
            // Editor draft; reset to the saved value whenever it changes.
            descriptionDraft: this.plan.description || "",
            DESCRIPTION_MAX_CHARS: PLAN_DESCRIPTION_MAX_CHARS,
            SHIPPING_MARKUP_PCT_MAX,
            // Inline so the contrast spec reads what is drawn. White on the
            // accent only clears 3:1 on some accents (primary 3.08:1), so the
            // text must be LARGE per WCAG (>= 18.66px bold) to pass AA.
            PREVIEW_BAND_TEXT: { color: "#ffffff", fontSize: "19px", fontWeight: "700" },
            iconOptions: PLAN_ICONS,
            accentOptions: PLAN_ACCENTS,
        };
    },

    watch: {
        "plan.description"(saved) {
            this.descriptionDraft = saved || "";
        },
    },

    computed: {
        // The RESOLVED icon + accent (a stale stored key reads as the
        // constant fallback), shared with /pricing via planPresentation.
        presentation() {
            return resolvePlanPresentation(this.plan);
        },
        descriptionLength() {
            return planDescriptionLength(this.descriptionDraft);
        },
        descriptionTooLong() {
            return this.descriptionLength > PLAN_DESCRIPTION_MAX_CHARS;
        },
        // Dirty = differs from the saved value as the USER sees it: an
        // emptied editor (`<p></p>`) and an unset description are the same.
        descriptionDirty() {
            const norm = (v) => (planDescriptionLength(v) === 0 ? "" : v || "");
            return norm(this.descriptionDraft) !== norm(this.plan.description);
        },

        // The allowance lives on the per-active-store price: `included_stores`
        // is null when the live version has none, so there is nothing to edit.
        hasIncludedStores() {
            return this.plan.included_stores != null;
        },
        includedStoresValue() {
            return this.hasIncludedStores ? String(this.plan.included_stores) : "";
        },

        // The one plan new self-sign-ups land on. `?? false`: an older
        // payload without the field reads as "not the default".
        isDefaultSignup() {
            return this.plan.is_default_signup === true;
        },

        // ---- Shipping surcharge ------------------------------------------
        //
        // Both NOT NULL on the plan, but a payload without them (an older
        // API) renders blank rather than "undefined". `== null`, never
        // truthiness: 0 is a real zero surcharge.

        shippingMarkupValue() {
            const pct = this.plan.shipping_markup_pct;
            return pct == null ? "" : String(pct);
        },

        shippingHandlingDollars() {
            const cents = this.plan.shipping_flat_handling_cents;
            return cents == null ? "" : (cents / 100).toFixed(2);
        },

        // ---- Platform-fee override -------------------------------------
        //
        // `platform_fee_rate_override` is null (no override -> the global
        // rate), 0 (genuinely no platform fee), or a fraction like 0.02.
        // EVERY branch here tests `== null` rather than truthiness: 0 is
        // falsy in JS, and a truthiness test would render a configured zero
        // as "no override" -- telling the admin the plan charges the global
        // rate when it charges nothing.

        hasFeeOverride() {
            return this.plan.platform_fee_rate_override != null;
        },

        // The input value, in PERCENT. Empty string means "no override",
        // which is why an empty box and a typed 0 must stay distinguishable.
        feeOverridePercent() {
            if (!this.hasFeeOverride) return "";
            // Round the x100 to kill float artefacts (0.0395 * 100 would
            // otherwise render as 3.9500000000000006).
            return String(
                Math.round(this.plan.platform_fee_rate_override * 1e6) / 1e4,
            );
        },

        // The page-wide platform fee (PlatformFeeRate), in percent. Empty
        // when not loaded / not configured so the label never renders
        // "NaN%" or "null%". Same x100 rounding as feeOverridePercent.
        globalRatePercent() {
            const r = this.globalPlatformFeeRate;
            if (r == null || !Number.isFinite(Number(r))) return "";
            return String(Math.round(Number(r) * 1e6) / 1e4);
        },

        feeOverrideLabel() {
            if (!this.hasFeeOverride) {
                return this.globalRatePercent === ""
                    ? "Using the global rate"
                    : `Using the global rate ${this.globalRatePercent}%`;
            }
            if (Number(this.plan.platform_fee_rate_override) === 0) {
                return "No platform fee on this plan";
            }
            return `${this.feeOverridePercent}% on this plan`;
        },

        // Every subscription on the plan, whatever its state (trial included):
        // "3 subscribers: 1 active, 2 trial" / "No subscribers".
        subscriberCount() {
            return this.plan.subscriber_count || 0;
        },

        subscriberLabel() {
            const n = this.subscriberCount;
            if (n === 0) return "No subscribers";
            const byState = this.plan.subscribers_by_state || {};
            const known = SUBSCRIBER_STATE_ORDER.filter((s) => byState[s]);
            const unknown = Object.keys(byState).filter(
                (s) => byState[s] && !SUBSCRIBER_STATE_ORDER.includes(s),
            );
            const parts = [...known, ...unknown].map(
                (s) => `${byState[s]} ${SUBSCRIBER_STATE_LABELS[s] || s}`,
            );
            const head = `${n} subscriber${n === 1 ? "" : "s"}`;
            return parts.length ? `${head}: ${parts.join(", ")}` : head;
        },

        // Delete (archive) is enabled ONLY when no subscription of ANY state
        // is on the plan -- the exact condition the server's archive guard
        // enforces (it 409s otherwise), so the user never meets that error
        // path by accident.
        canDelete() {
            return this.subscriberCount === 0;
        },

        deleteBlockedReason() {
            const n = this.subscriberCount;
            return (
                `Can't delete: ${n} subscriber${n === 1 ? "" : "s"} still on ` +
                "this plan. Reassign them or disable the plan first."
            );
        },

        priceDollars() {
            const cents = this.plan.monthly_price_cents;
            return cents != null ? (cents / 100).toFixed(2) : "";
        },

        // Header price: every nonzero part (flat monthly fee and/or
        // per-active-store price) via the shared price-line helper, so this
        // card, /pricing and signup state a plan's price identically.
        pricePartList() {
            return planPriceParts({
                monthlyCents: this.plan.monthly_price_cents,
                perActiveCents: this.plan.per_active_store_cents,
            });
        },

        appFeeLabel() {
            if (this.appFeeBasisPoints == null) return "--";
            return `${(this.appFeeBasisPoints / 100).toFixed(2)}%`;
        },

        hasGrids() {
            return !!(
                this.gridTier &&
                Array.isArray(this.gridTier.grids) &&
                this.gridTier.grids.length > 0
            );
        },

        // Embroidery first, DTF second, so the two columns always read in a
        // stable left-to-right order regardless of endpoint ordering.
        orderedGrids() {
            if (!this.hasGrids) return [];
            const order = { embroidery: 0, dtf: 1 };
            return [...this.gridTier.grids].sort(
                (a, b) =>
                    (order[a.decoration_type] ?? 99) -
                    (order[b.decoration_type] ?? 99),
            );
        },
    },

    methods: {
        formatCents,

        qtyLabel(row) {
            const min = row.qty_min;
            const max = row.qty_max;
            if (max === null || max === undefined) return `${min}+`;
            return `${min}-${max}`;
        },

        embroideryBands(grid) {
            const first = grid.qty_tiers && grid.qty_tiers[0];
            const bands = (first && first.stitch_tiers) || [];
            return bands.map((b) => {
                const min = b.stitch_min;
                const max = b.stitch_max;
                const label =
                    max === null || max === undefined
                        ? `${min}+`
                        : `${min}-${max}`;
                return { key: `${min}-${max}`, label };
            });
        },

        onToggleExpand() {
            this.$emit("toggle-expand", this.plan.id);
        },

        // Icon / accent: emit only a CHANGE; the parent PATCHes the one key.
        onPickIcon(key) {
            if (key === this.presentation.iconKey) return;
            this.$emit("update-presentation", this.plan.id, { icon_key: key });
        },
        onPickAccent(key) {
            if (key === this.presentation.accentKey) return;
            this.$emit("update-presentation", this.plan.id, { accent_key: key });
        },

        onToggleActive() {
            // Emit the INTENDED next state; the parent decides whether the
            // transition needs a reassign modal (disabling with subscribers).
            this.$emit("toggle-active", this.plan.id, this.plan.is_active === false);
        },

        onTogglePublic() {
            this.$emit("toggle-public", this.plan.id, this.plan.is_public === false);
        },

        onSetDefaultSignup() {
            if (this.isDefaultSignup) return;
            this.$emit("set-default-signup", this.plan.id);
        },

        onDelete() {
            if (!this.canDelete) return;
            this.$emit("delete-plan", this.plan.id);
        },

        // Supplier toggles emit the TIER they act on, not the plan: the row
        // being written is the tier's, and several plans can share it. A
        // plan-keyed emit would make the parent guess which tier row a plan
        // meant, and two cards on one tier would race to answer differently.
        onSupplierToggle(supplier, event) {
            if (!this.supplierTier) return;
            this.$emit(
                "toggle-supplier",
                this.supplierTier.tier_key,
                supplier.supplier_key,
                event.target.checked,
            );
        },

        onSaveSuppliers() {
            if (!this.supplierTier) return;
            this.$emit("save-suppliers", this.supplierTier.tier_key);
        },

        // Emits the TIER, not the plan -- the flag belongs to the tier, and
        // several plans can share it. Same reason the supplier toggles do.
        onWhiteLabelToggle(event) {
            if (!this.tierKey) return;
            this.$emit(
                "toggle-white-label",
                this.tierKey,
                !!event.target.checked,
            );
        },

        onNameBlur(event) {
            const value = (event.target.value || "").trim();
            // A plan cannot be un-named: put the saved name back.
            if (!value) {
                event.target.value = this.plan.name || "";
                return;
            }
            if (value === this.plan.name) return;
            this.$emit("update-name", this.plan.id, value);
        },

        // Whole days, 0..365 (the API rejects anything else). Blank, negative,
        // fractional, junk or out-of-range is not a trial length: restore the
        // saved value rather than emit.
        onTrialDaysBlur(event) {
            const raw = (event.target.value || "").trim();
            const saved = this.plan.trial_days;
            event.target.value = saved == null ? "" : String(saved);
            if (!/^\d+$/.test(raw)) return;
            const days = Number(raw);
            if (days > 365 || days === saved) return;
            this.$emit("update-trial-days", this.plan.id, days);
        },

        onSaveDescription() {
            if (!this.descriptionDirty || this.descriptionTooLong) return;
            this.$emit("update-description", this.plan.id, this.descriptionDraft);
        },

        // Whole stores, >= 0. Blank, negative, fractional or junk is not an
        // allowance: restore the saved value rather than emit.
        onIncludedStoresBlur(event) {
            const raw = (event.target.value || "").trim();
            const saved = this.plan.included_stores;
            event.target.value = saved == null ? "" : String(saved);
            if (!this.hasIncludedStores || !/^\d+$/.test(raw)) return;
            const count = Number(raw);
            if (count === saved) return;
            this.$emit("update-included-stores", this.plan.id, count);
        },

        // `== null` keeps a real $0 ("0.00") distinct from "no charge" ("").
        storeClassDollars(storeClass) {
            const cents =
                storeClass === "full"
                    ? this.plan.per_active_store_cents
                    : this.plan.per_dormant_store_cents;
            return cents == null ? "" : (cents / 100).toFixed(2);
        },

        // Blank, negative or junk is not a price: put the saved value back
        // rather than emit. Otherwise the field is reset to the SAVED value
        // too -- the parent's reply re-renders it with the new amount on
        // success, and on failure it correctly keeps showing what is saved.
        onStoreClassPriceBlur(storeClass, event) {
            const raw = (event.target.value || "").trim();
            const saved = this.storeClassDollars(storeClass);
            const cents = raw === "" ? null : dollarsToCents(raw);
            event.target.value = saved;
            if (cents == null || cents < 0) {
                return;
            }
            const current =
                storeClass === "full"
                    ? this.plan.per_active_store_cents
                    : this.plan.per_dormant_store_cents;
            if (cents === (current ?? null)) return;
            this.$emit("update-store-class-price", this.plan.id, storeClass, cents);
        },

        // The flat monthly fee. Same guard as the per-store prices: a blank,
        // unparseable or negative entry is a slip, not an instruction, so the
        // box goes back to the saved fee and nothing is emitted (a null here
        // PUBLISHES a plan version with no flat fee). A typed "0.00" is a real
        // $0 fee. Taking the fee away on purpose is onRemovePrice below.
        onPriceBlur(event) {
            const raw = (event.target.value || "").trim();
            const cents = dollarsToCents(raw);
            event.target.value = this.priceDollars;
            if (cents == null || cents < 0) return;
            // No-op if unchanged.
            if (cents === (this.plan.monthly_price_cents ?? null)) return;
            this.$emit("update-price", this.plan.id, cents);
        },

        // The one deliberate way to end a flat fee: publishes a new plan
        // version without it (pinned subscribers keep theirs).
        onRemovePrice() {
            if (this.plan.monthly_price_cents == null) return;
            this.$emit("update-price", this.plan.id, null);
        },

        // Percent in the box -> FRACTION on the wire. An empty box clears
        // the override; a typed 0 sets a real zero. `Number("") === 0` in
        // JS, so the empty check MUST come first and be a string test --
        // this is the falsy-zero trap in its JavaScript form.
        onFeeOverrideBlur(event) {
            const raw = (event.target.value || "").trim();

            let next;
            if (raw === "") {
                next = null;
            } else {
                const pct = Number(raw);
                // Reject junk and out-of-range client-side; the server caps
                // at 0.5 (50%) too. Silently no-op rather than emit garbage.
                if (!Number.isFinite(pct) || pct < 0 || pct > 50) return;
                // Round at 4dp to match the NUMERIC(5,4) column.
                next = Math.round((pct / 100) * 1e6) / 1e6;
            }

            const current = this.plan.platform_fee_rate_override ?? null;
            // `==` on null covers undefined too; numbers compare by value.
            if (next === current) return;
            this.$emit("update-fee-override", this.plan.id, next);
        },

        onClearFeeOverride() {
            if (!this.hasFeeOverride) return;
            this.$emit("update-fee-override", this.plan.id, null);
        },

        // Shipping markup: a whole percent 0..100 (the engine reads an int).
        // Blank, negative, fractional, junk or out-of-range is a slip: put
        // the saved value back rather than emit. The box is always reset to
        // the SAVED value; the parent's reply re-renders it on success.
        onShippingMarkupBlur(event) {
            const raw = (event.target.value || "").trim();
            event.target.value = this.shippingMarkupValue;
            if (!/^\d+$/.test(raw)) return;
            const pct = Number(raw);
            if (pct > SHIPPING_MARKUP_PCT_MAX) return;
            if (pct === this.plan.shipping_markup_pct) return;
            this.$emit("update-shipping-surcharge", this.plan.id, {
                shipping_markup_pct: pct,
            });
        },

        // Shipping handling fee: dollars in the box, CENTS on the wire.
        // dollarsToCents is strict (null for blank or junk); a negative or
        // over-$100.00 entry is refused here as well as by the API.
        onShippingHandlingBlur(event) {
            const raw = (event.target.value || "").trim();
            event.target.value = this.shippingHandlingDollars;
            const cents = dollarsToCents(raw);
            if (cents == null || cents < 0 || cents > SHIPPING_HANDLING_CENTS_MAX) {
                return;
            }
            if (cents === this.plan.shipping_flat_handling_cents) return;
            this.$emit("update-shipping-surcharge", this.plan.id, {
                shipping_flat_handling_cents: cents,
            });
        },
    },
};
</script>

<style scoped>
.plan-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin-bottom: 12px;
    overflow: hidden;
}
.plan-card__field-hint {
    font-size: 12px;
    color: #4a5568;
}
.plan-card__description {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 640px;
    margin: 4px 0 12px;
}
.plan-card__plan-icon {
    flex: 0 0 auto;
}
.plan-card__presentation {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 640px;
    margin: 4px 0 12px;
}
.plan-card__presentation-sub {
    font-size: 12px;
    font-weight: 600;
    color: var(--tf-fg-3);
}
.plan-card__presentation-preview {
    width: 260px;
    border: 1px solid #cbd5e0;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 10px;
    gap: 10px;
    background: #fff;
}
.plan-card__presentation-preview-band {
    align-self: stretch;
    background: var(--plan-accent);
    text-transform: uppercase;
    text-align: center;
    padding: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.plan-card__icon-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.plan-card__icon-option {
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    background: #fff;
    color: #1a202c;
    cursor: pointer;
    font-size: 22px;
}
.plan-card__icon-option--on {
    border: 2px solid var(--tf-primary, #119ae9);
    background: #e8f4fd;
}
.plan-card__accent-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.plan-card__accent-option {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px #cbd5e0;
    color: #fff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
}
.plan-card__accent-option--on {
    box-shadow: 0 0 0 2px #1a202c;
}
.plan-card__icon-option:focus-visible,
.plan-card__accent-option:focus-visible {
    outline: 2px solid var(--tf-primary, #119ae9);
    outline-offset: 2px;
}
.plan-card__description-actions {
    display: flex;
    gap: 8px;
}
.plan-card__store-class-hint {
    margin: 4px 0 12px;
    font-size: 12px;
    color: #4a5568;
}
.plan-card--new {
    border-color: #90cdf4;
}
.plan-card--disabled {
    opacity: 0.75;
}
.plan-card__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    cursor: pointer;
}
.plan-card__reorder {
    display: flex;
    align-items: center;
    gap: 2px;
    flex: none;
}
.plan-card__drag {
    cursor: grab;
    color: #64748b;
    font-size: 20px;
}
.plan-card__move-btn {
    display: inline-flex;
    align-items: center;
    background: none;
    border: 0;
    padding: 2px;
    border-radius: 4px;
    color: #4a5568;
    cursor: pointer;
}
.plan-card__move-btn .tf-icon {
    font-size: 18px;
}
.plan-card__move-btn:hover:not(:disabled) {
    background: #edf2f7;
}
.plan-card__move-btn:disabled {
    color: #cbd5e0;
    cursor: default;
}
.plan-card__header--expanded {
    border-bottom: 1px solid #edf2f7;
}
.plan-card__heading {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}
.plan-card__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.plan-card__title {
    font-size: 16px;
    font-weight: 600;
    color: #1a202c;
}
.plan-card__subtext {
    font-size: 12px;
    color: var(--tf-fg-3);
}
.plan-card__pill {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 8px;
    border-radius: 999px;
}
.plan-card__pill--hidden {
    background: #fefcbf;
    color: #744210;
    border: 1px solid #f6e05e;
}
.plan-card__pill--disabled {
    background: #fed7d7;
    color: #742a2a;
    border: 1px solid #fc8181;
}
.plan-card__pill--new {
    background: #ebf8ff;
    color: #2b6cb0;
    border: 1px solid #90cdf4;
}
.plan-card__pill--default {
    background: #f0fff4;
    color: #276749;
    border: 1px solid #9ae6b4;
}
.plan-card__default-signup {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 12px 0;
}
.plan-card__default-signup-hint {
    font-size: 12px;
    color: var(--tf-fg-4);
}
.plan-card__toggle-btn:disabled {
    cursor: default;
}
.plan-card__spacer {
    flex: 1 1 auto;
}
.plan-card__price {
    font-size: 15px;
    font-weight: 600;
    color: #1a202c;
    white-space: nowrap;
}
.plan-card__price-per {
    font-size: 11px;
    font-weight: 400;
    color: var(--tf-fg-4);
}
.plan-card__toggle-btn,
.plan-card__delete-btn,
.plan-card__expand-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 1px solid #cbd5e0;
    background: #f7fafc;
    color: #4a5568;
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
}
.plan-card__toggle-btn--on {
    background: #ebf8ff;
    border-color: #90cdf4;
    color: #2b6cb0;
}
.plan-card__delete-btn {
    color: #c53030;
    border-color: #feb2b2;
    background: #fff5f5;
}
.plan-card__delete-btn:disabled {
    color: #a0aec0;
    border-color: #e2e8f0;
    background: #f7fafc;
    cursor: not-allowed;
}
.plan-card__expand-btn {
    padding: 5px 8px;
}
.plan-card__body {
    padding: 16px;
    background: #fafbfc;
}
.plan-card__fields {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}
.plan-card__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 180px;
}
.plan-card__field-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--tf-fg-3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.plan-card__input {
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    padding: 6px 10px;
    font-size: 14px;
    color: #1a202c;
    background: #fff;
}
.plan-card__input-dollar {
    display: flex;
    align-items: center;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    background: #fff;
    overflow: hidden;
}
.plan-card__input-dollar-prefix {
    padding: 6px 8px 6px 10px;
    font-size: 14px;
    color: #4a5568;
    background: #f7fafc;
    border-right: 1px solid #e2e8f0;
    user-select: none;
}
.plan-card__input--dollar {
    border: none;
    border-radius: 0;
    flex: 1;
}
.plan-card__fee {
    font-size: 14px;
    color: #1a202c;
    padding: 6px 0;
}
.plan-card__fee-override-state {
    font-size: 12px;
    color: #4a5568;
    margin-top: 4px;
}
.plan-card__link-btn {
    align-self: flex-start;
    margin-top: 2px;
    padding: 0;
    border: none;
    background: none;
    color: #2b6cb0;
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
}
.plan-card__error {
    font-size: 13px;
    color: #c53030;
    margin: 0 0 12px;
}
.plan-card__grids-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 10px;
    flex-wrap: wrap;
}
.plan-card__grids-title {
    font-size: 13px;
    font-weight: 600;
    color: #2d3748;
}
.plan-card__grids-hint {
    font-size: 12px;
    color: var(--tf-fg-4);
}
.plan-card__grids-empty {
    font-size: 13px;
    color: var(--tf-fg-4);
    margin: 0;
}
.plan-card__grids {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    align-items: flex-start;
}
/*
  Size each grid column to its CONTENT, not to an equal half (Jake
  feedback 2026-08-04): the embroidery table has 7 columns and the DTF
  table only 2, so equal-width halves stretched the 2-column DTF table
  and left a lot of whitespace. `flex: 0 1 auto` + a content-width table
  (below) lets each table be exactly as wide as its columns need; they
  sit left-aligned with the 24px gap between, and wrap on narrow
  viewports (flex-wrap on the parent).
*/
.plan-card__grid-col {
    flex: 0 1 auto;
    min-width: 0;
}
.plan-card__grid-block-title {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
    margin: 0 0 6px;
}
.plan-card__grid-table {
    width: auto;
    border-collapse: collapse;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
}
.plan-card__grid-table th,
.plan-card__grid-table td {
    font-size: 12px;
    padding: 6px 10px;
    text-align: right;
    white-space: nowrap;
    border-bottom: 1px solid #edf2f7;
}
.plan-card__grid-table th {
    background: #f7fafc;
    color: #2d3748;
    font-weight: 600;
}
.plan-card__grid-table th:first-child,
.plan-card__grid-table td:first-child {
    text-align: left;
}

/* --- Supplier availability (per plan) ------------------------------- */
/*
  Sits ABOVE the decoration grids in the body: "which suppliers can this
  plan sell" is a coarser question than the per-stitch price grid, and the
  supplier list is the one the SA came to change (Jake 2026-08-29).
*/
.plan-card__suppliers-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 10px;
    flex-wrap: wrap;
}
.plan-card__suppliers {
    width: auto;
    min-width: 280px;
    border-collapse: collapse;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 4px;
}
.plan-card__suppliers th {
    text-align: left;
    background: #f7fafc;
    color: #2d3748;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 12px;
    border-bottom: 1px solid #e2e8f0;
}
.plan-card__suppliers td {
    font-size: 13px;
    padding: 6px 12px;
    border-bottom: 1px solid #edf2f7;
    vertical-align: middle;
}
.plan-card__suppliers th:last-child,
.plan-card__suppliers td:last-child {
    text-align: center;
    width: 80px;
}
.plan-card__supplier-key {
    font-size: 11px;
    color: var(--tf-fg-4);
}
.plan-card__suppliers-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 10px 0 20px;
}
.plan-card__suppliers-save {
    border-radius: 4px;
    padding: 6px 14px;
    font-size: 13px;
    cursor: pointer;
    border: 1px solid #3182ce;
    background: #3182ce;
    color: #fff;
}
.plan-card__suppliers-save:disabled {
    background: #edf2f7;
    border-color: #cbd5e0;
    color: #a0aec0;
    cursor: default;
}
.plan-card__suppliers-saved {
    font-size: 13px;
    color: #276749;
}

/* --- White-label branding (per plan) --------------------------------- */
/*
  Sits directly under supplier availability: both answer "what does this
  plan INCLUDE", and keeping them adjacent is what makes the card read as
  one list of entitlements rather than a stack of unrelated controls.
*/
.plan-card__wl-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin: 18px 0 10px;
    flex-wrap: wrap;
}
.plan-card__wl-switch {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #2d3748;
    white-space: nowrap;
}
.plan-card__wl-saved {
    font-size: 13px;
    color: #276749;
}
</style>
